import { useMemo } from 'react';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import NextDividend from '../NextDividend';
import type { PayoutEntity } from '@/components/PayoutRatio';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import { useTracking } from 'react-tracking';
import {
  COMPANY_CURRENT_PAYOUT,
  COMPANY_DIVIDEND_YIELD_AND_PAYMENT,
  COMPANY_FUTURE_PAYOUT,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
import {
  DIVIDEND_PAYOUT_CURRENT,
  DIVIDEND_PAYOUT_FUTURE,
  DIVIDEND_STABILITY_GROWTH,
  DIVIDEND_YIELD,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
import loadable from '@loadable/component';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import DividendYieldVsMarketTable from '../DividendYieldVsMarketTable';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import { TableWrapper } from './styled';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import Introduction from '@/pages/CompanyReport/components/Introduction';

import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { useDividendsData } from '../StabilityAndGrowthOfPayments/hooks/useDividendsData';
import { getKeyInfoDPS } from '../StabilityAndGrowthOfPayments/containers/utils';
import { formatterCurrencyTruncated } from '@/utilities/format';
import {
  DIVIDEND_STATEMENTS_DEFAULT,
  DIVIDEND_STATEMENTS_FINANCIAL,
} from '@/pages/CompanyReport/constants';
import { getStatementChecks } from '../../../Introduction/utils';
import { useDividendTranslation } from '../../hooks/useDividendTranslations';
import { Heading } from '@simplywallst/ui-core';
import { TranslationWrapper } from '../StabilityAndGrowthOfPayments/components/TranslationWrapper/TranslationWrapper';
import { useVisibleSectionsIsPristine } from '../../../VisibleSectionsContextProvider';

const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const PayoutRatio = loadable(() => import('@/components/PayoutRatio'), {
  resolveComponent: (c) => c.PayoutRatio,
  ssr: false,
});

const DividendYieldVsMarket = loadable(
  () => import('../DividendYieldVsMarket'),
  { ssr: false }
);

const StabilityAndGrowthOfPaymentsTable = loadable(
  () => import('../StabilityAndGrowthOfPaymentsTable'),
  { ssr: false }
);

const CriteriaChecks = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/CriteriaChecks'
    )
);
const KeyInformation = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/KeyInformation'
    )
);
const RecentSectionUpdates = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/RecentSectionUpdates'
    )
);

const NextStep = loadable(
  () => import('@/pages/CompanyReport/components/SimilarCompanies/NextStep')
);

export interface Props {
  isFund: boolean;
  companyId: string;
  companyName: string;
  sectionNumber?: number;
  dividendYield: number | null;
  dividendYieldFuture: number | null;
  dividendYieldFuture3Y: number | null;
  dividendNextDate: number;
  dividendNextDateFormatted: string;
  showUpcomingDividendPayment: boolean;
  dividendNextPayDate: number;
  dividendNextPayDateFormatted: string;
  industryName: string;
  marketName: string;
  marketDividendYieldP25: number;
  marketDividendYieldP75: number;
  industryDividendYield: number;
  dividendYieldFormatted: string;
  marketDividendYieldP25Formatted: string;
  marketDividendYieldP75Formatted: string;
  industryDividendYieldFormatted: string;
  dividendYieldFutureFormatted: string;
  dividendYieldFuture3YFormatted: string;
  currentDividendPayout: PayoutEntity[];
  currentDividendPayoutRatio: number;
  currentDividendPayoutRatioFormatted: string;
  futureDividendPayout: PayoutEntity[];
  futureDividendPayoutRatio: number;
  futureDividendPayoutRatioFormatted: string;
  cashDividendPayout: PayoutEntity[];
  cashDividendPayoutRatio: number;
  cashDividendPayoutRatioFormatted: string;
  lastPaymentDividendCurrency: number;
  tradingItemCurrencyISO: string;
  viewMode: ViewMode;
  buybackYieldFormatted: string;
  totalShareholderYieldFormatted: string;
  dividendPaymentsGrowthAnnualFormatted: string;
}

export const Dividend = ({
  buybackYieldFormatted,
  totalShareholderYieldFormatted,
  dividendPaymentsGrowthAnnualFormatted,
  isFund,
  companyId,
  companyName,
  sectionNumber,
  dividendYield,
  dividendYieldFuture3Y,
  marketDividendYieldP25,
  marketDividendYieldP75,
  industryDividendYield,
  dividendNextDate,
  dividendNextDateFormatted,
  dividendNextPayDate,
  dividendNextPayDateFormatted,
  showUpcomingDividendPayment,
  industryName,
  marketName,
  dividendYieldFormatted,
  marketDividendYieldP25Formatted,
  marketDividendYieldP75Formatted,
  industryDividendYieldFormatted,
  dividendYieldFuture3YFormatted,
  currentDividendPayout,
  currentDividendPayoutRatio,
  currentDividendPayoutRatioFormatted,
  futureDividendPayout,
  futureDividendPayoutRatio,
  futureDividendPayoutRatioFormatted,
  cashDividendPayout,
  cashDividendPayoutRatio,
  cashDividendPayoutRatioFormatted,
  viewMode,
}: Props) => {
  const {
    dividendYieldDownloadUrl,
    currentPayoutDownloadUrl,
    futurePayoutDownloadUrl,
  } = useMemo(() => {
    return {
      dividendYieldDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.HistoricDividend
      ),
      currentPayoutDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.CurrentDividendPayout
      ),
      futurePayoutDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.FutureDividendPayout
      ),
    };
  }, [companyId]);

  const canonicalUrl = useCompanyCanonicalUrl();
  const dateIn3Y = (new Date().getUTCFullYear() + 3).toString();
  const isPristine = useVisibleSectionsIsPristine();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const term = useGlossaryTerms();
  const { data: divPaymentDetailData, status } = useDividendsData(companyId);
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });
  const {
    IsDividendSignificant,
    IsDividendYieldTopTier,
    IsDividendStable,
    IsDividendGrowing,
    IsDividendCovered,
    IsDividendCoveredIn3Years,
    IsDividendCoveredByFreeCashFlow,
    IsBank,
  } = statements || {};
  const keyInfoDPS = getKeyInfoDPS(divPaymentDetailData);

  const isBank = IsBank?.value === true;

  const t = useDividendTranslation();

  const statementChecks = getStatementChecks(
    isBank ? DIVIDEND_STATEMENTS_FINANCIAL : DIVIDEND_STATEMENTS_DEFAULT,
    statements
  );

  const { Track } = useTracking({
    type: 'track',
    subject: 'dividends',
    section: 'dividends',
  });

  const dividendYieldVsMarketLabels = useMemo(() => {
    return {
      key0: t('dividendYieldVsMarket.chart.key0'),
      key1: t('dividendYieldVsMarket.chart.key1'),
      key2: t('dividendYieldVsMarket.chart.key2'),
      key3: t('dividendYieldVsMarket.chart.key3'),
      key4: t('dividendYieldVsMarket.chart.key4'),
      tooltipKey4: t('dividendYieldVsMarket.chart.tooltip5', {
        company: companyName,
      }),
      bottomLabel: t('dividendYieldVsMarket.chart.bottomLabel'),
    };
  }, [t, companyName]);

  return (
    <Track>
      <ReportSection
        sectionNumber={sectionNumber}
        data-cy-id="dividend-section"
      >
        <CompanySectionTitle
          title={t('heading')}
          pristineTitle={t('pristineHeading', { company: companyName })}
        />
        <Introduction
          scoreName={t('heading')}
          criteriaChecks={
            <CriteriaChecks
              scoreName={t('heading')}
              checks={statementChecks}
              titleElement={subSectionTitleElement}
            />
          }
          keyInformation={
            <KeyInformation
              heading={
                <Heading as={subSectionTitleElement} typography="small">
                  {t('keyInfo.heading')}
                </Heading>
              }
              keyMetrics={[
                {
                  title: t('keyInfo.dividendYield'),
                  value: dividendYieldFormatted,
                  tooltip: term('current dividend yield'),
                },
                {
                  title: t('keyInfo.buybackYield'),
                  value: buybackYieldFormatted,
                  tooltip: term('buyback yield'),
                },
              ]}
              metrics={[
                {
                  title: t('keyInfo.totalShareholderYield'),
                  value: totalShareholderYieldFormatted,
                  tooltip: term('total shareholder yield'),
                },
                {
                  title: t('keyInfo.dividendYieldForecast3Y'),
                  value: dividendYieldFuture3YFormatted,
                  tooltip: term('future dividend yield'),
                },
                {
                  title: t('keyInfo.dividendPaymentsGrowthAnnual'),
                  value: dividendPaymentsGrowthAnnualFormatted,
                  tooltip: term('dividend payments growth annual'),
                },
                {
                  title: t('keyInfo.nextDividendPayDate'),
                  value: dividendNextPayDateFormatted,
                },
                {
                  title: t('keyInfo.exDividendDate'),
                  value: dividendNextDateFormatted,
                  tooltip: term('ex dividend date'),
                },
                {
                  title: t('keyInfo.dividendPerShare'),
                  value: formatterCurrencyTruncated(
                    keyInfoDPS,
                    divPaymentDetailData?.listingCurrencyISO || '',
                    3
                  ),
                  tooltip: term('dividend per share'),
                },
                {
                  title: t('keyInfo.payoutRatio'),
                  value: currentDividendPayoutRatioFormatted,
                  tooltip: term('payout ratio'),
                },
              ]}
            />
          }
          recentSectionUpdates={
            <RecentSectionUpdates
              lookup={canonicalUrl}
              title={t('recentUpdates.heading')}
              filterByEventCategory="dividend"
            />
          }
        />
        {showUpcomingDividendPayment && (
          <>
            <ReportSubSection
              titleElement={subSectionTitleElement}
              title={t('subSection.1.heading')}
              trackingProps={{ subject: 'chart' }}
              body={
                <NextDividend
                  dividendNextDate={dividendNextDate}
                  dividendNextPayDate={dividendNextPayDate}
                />
              }
            />
            <hr />
          </>
        )}

        <BrazeContentCardWrapper>
          <BrazeContentCard
            feedType="company-overview.dividend"
            anchorTarget="_blank"
          />
        </BrazeContentCardWrapper>
        <PageAnchor type="sub-section-title" id={DIVIDEND_STABILITY_GROWTH} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t('subSection.2.heading')}
          trackingProps={{ subject: 'chart' }}
          body={
            <ChartWrapper
              id="dividend-stability-and-growth-of-payments-chart"
              chart={<TranslationWrapper data={divPaymentDetailData} />}
              table={
                <TableWrapper>
                  <StabilityAndGrowthOfPaymentsTable
                    viewMode={viewMode}
                    companyName={companyName}
                    companyId={companyId}
                  />
                </TableWrapper>
              }
              feedbackLabel={COMPANY_DIVIDEND_YIELD_AND_PAYMENT}
              exportDarkUrl={dividendYieldDownloadUrl}
              isLoading={status === 'pending'}
              loadingText={t('stabilityAndGrowth.loadingText')}
              preRenderHeightSmDown={460}
              preRenderHeight={480}
            />
          }
          statements={
            <>
              <Statement
                title={IsDividendStable?.title}
                description={<Description statement={IsDividendStable} />}
                value={IsDividendStable?.value}
                statementId={CompanyStatements.dividendStatement3}
              />
              <Statement
                title={IsDividendGrowing?.title}
                description={<Description statement={IsDividendGrowing} />}
                value={IsDividendGrowing?.value}
                statementId={CompanyStatements.dividendStatement4}
              />
            </>
          }
        />
        <hr />
        <PageAnchor type="sub-section-title" id={DIVIDEND_YIELD} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t('subSection.3.heading')}
          trackingProps={{ subject: 'chart' }}
          body={
            <ChartWrapper
              id="dividend-yield-vs-market-chart"
              chart={
                <DividendYieldVsMarket
                  companyName={companyName}
                  industryName={industryName}
                  marketName={marketName}
                  dividendYield={dividendYield}
                  dividendYieldFuture={dividendYieldFuture3Y}
                  marketDividendYieldP25={marketDividendYieldP25}
                  marketDividendYieldP75={marketDividendYieldP75}
                  industryDividendYield={industryDividendYield}
                  dividendYieldFormatted={dividendYieldFormatted}
                  marketDividendYieldP25Formatted={
                    marketDividendYieldP25Formatted
                  }
                  marketDividendYieldP75Formatted={
                    marketDividendYieldP75Formatted
                  }
                  industryDividendYieldFormatted={
                    industryDividendYieldFormatted
                  }
                  dividendYieldFutureFormatted={dividendYieldFuture3YFormatted}
                  labels={dividendYieldVsMarketLabels}
                  titleElement={isPristine ? 'h3' : 'h4'}
                />
              }
              table={<DividendYieldVsMarketTable />}
              preRenderHeight={300}
            />
          }
          statements={
            <>
              <Statement
                title={IsDividendSignificant?.title}
                description={<Description statement={IsDividendSignificant} />}
                value={IsDividendSignificant?.value}
                statementId={CompanyStatements.dividendStatement1}
              />
              <Statement
                title={IsDividendYieldTopTier?.title}
                description={<Description statement={IsDividendYieldTopTier} />}
                value={IsDividendYieldTopTier?.value}
                statementId={CompanyStatements.dividendStatement2}
              />
            </>
          }
        />
        <PageAnchor type="sub-section-title" id={DIVIDEND_PAYOUT_CURRENT} />
        <hr />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t(
            isBank ? 'subSection.4.heading.bank' : 'subSection.4.heading'
          )}
          trackingProps={{ subject: 'chart' }}
          split={true}
          body={
            <ChartWrapper
              id="dividend-earnings-payout-chart"
              preRenderHeight="205px"
              chart={
                <PayoutRatio
                  data-cy-id="chart-payout-to-shareholders"
                  data={currentDividendPayout}
                  payoutRatio={currentDividendPayoutRatio}
                  payoutRatioFormatted={currentDividendPayoutRatioFormatted}
                  payoutThreshold={0.9} // TODO: Need this supplied by the analysis ideally
                  when={isBank ? 'Current' : undefined}
                  labels={{
                    payoutRatioIsNegative: isBank
                      ? t('pie.label.payoutRatio.negative.isBank')
                      : t('pie.label.payoutRatio.negative'),
                    payoutRatioIsZero: t('pie.label.payoutRatio.zero'),
                    noData: t('pie.label.payoutRatio.noData'),
                    retained: t('pie.label.payoutRatio.retained.earnings'),
                    paid: t('pie.label.payoutRatio.default'),
                  }}
                />
              }
              feedbackLabel={COMPANY_CURRENT_PAYOUT}
              exportDarkUrl={currentPayoutDownloadUrl}
            />
          }
          statements={
            <Statement
              title={IsDividendCovered?.title}
              description={<Description statement={IsDividendCovered} />}
              value={IsDividendCovered?.value}
              statementId={CompanyStatements.dividendStatement5}
            />
          }
        />
        <PageAnchor type="sub-section-title" id={DIVIDEND_PAYOUT_FUTURE} />
        <hr />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t(
            isBank ? 'subSection.5.heading.bank' : 'subSection.5.heading'
          )}
          trackingProps={{ subject: 'chart' }}
          split={true}
          body={
            <ChartWrapper
              id="dividend-cash-payout-chart"
              preRenderHeight="205px"
              chart={
                <PayoutRatio
                  data-cy-id="chart-payout-to-shareholders"
                  data={isBank ? futureDividendPayout : cashDividendPayout}
                  payoutRatio={
                    isBank ? futureDividendPayoutRatio : cashDividendPayoutRatio
                  }
                  payoutRatioFormatted={
                    isBank
                      ? futureDividendPayoutRatioFormatted
                      : cashDividendPayoutRatioFormatted
                  }
                  payoutThreshold={0.9} // TODO: Need this supplied by the analysis ideally
                  labels={{
                    payoutRatioIsNegative: isBank
                      ? t('pie.label.payoutRatio.negative.isBank')
                      : t('pie.label.payoutRatio.negative'),
                    payoutRatioIsZero: t('pie.label.payoutRatio.zero'),
                    noData: t('pie.label.payoutRatio.noData'),
                    retained: isBank
                      ? t('pie.label.payoutRatio.retained.earnings')
                      : t('pie.label.payoutRatio.retained.cashFlow'),
                    paid: t('pie.label.payoutRatio.default'),
                  }}
                  when={isBank ? dateIn3Y : undefined}
                />
              }
              feedbackLabel={COMPANY_FUTURE_PAYOUT}
              exportDarkUrl={isBank ? futurePayoutDownloadUrl : undefined}
            />
          }
          statements={
            isBank ? (
              <Statement
                title={IsDividendCoveredIn3Years?.title}
                description={
                  <Description statement={IsDividendCoveredIn3Years} />
                }
                value={IsDividendCoveredIn3Years?.value}
                statementId={CompanyStatements.dividendStatement6}
              />
            ) : (
              <Statement
                title={IsDividendCoveredByFreeCashFlow?.title}
                description={
                  <Description statement={IsDividendCoveredByFreeCashFlow} />
                }
                value={IsDividendCoveredByFreeCashFlow?.value}
                statementId={CompanyStatements.dividendStatement7}
              />
            )
          }
        />
        {isFund === false && (
          <>
            <hr />
            <NextStep section="dividend" title={t('subSection.6.heading')} />
          </>
        )}
      </ReportSection>
    </Track>
  );
};
